import { BuildingSummary } from '../types/protocols';
import { MaintenancePlanActivation, TimelineEvent, TimelineEventType } from '../types/timeline';
import { parseDateString } from './timelineUtils';

/**
 * Maintenance plan activations for the timeline, derived from the plans
 * themselves: the plan in maintenance/active/{b} and every plan in
 * maintenance/archive/{b}/* carry metadata.activatedAt / activatedBy. Nothing
 * is stored for the timeline. Archiving is not an event.
 *
 * Re-activations to fix something just missed are folded in: a run of
 * activations with no gap longer than REACTIVATION_WINDOW_DAYS is one event,
 * showing the last activation of the run and how many it covers.
 */

export const REACTIVATION_WINDOW_DAYS = 7;

/** True when `later` is at most REACTIVATION_WINDOW_DAYS calendar days after `earlier`. */
const withinReactivationWindow = (earlier: Date, later: Date): boolean => {
    const windowEnd = new Date(earlier);
    windowEnd.setDate(windowEnd.getDate() + REACTIVATION_WINDOW_DAYS);
    return later.getTime() <= windowEnd.getTime();
};

interface PlanActivationFacts {
    activatedAt: string;
    activatedBy?: string;
    componentCount: number;
    activityCount: number;
    isCurrent: boolean;
}

const parsePlan = (data: unknown, isCurrent: boolean): PlanActivationFacts | null => {
    if (!data || typeof data !== 'object') return null;
    const plan = data as Record<string, unknown>;
    const metadata = plan.metadata as Record<string, unknown> | undefined;
    if (!metadata || typeof metadata !== 'object') return null;
    const activatedAt = metadata.activatedAt;
    if (typeof activatedAt !== 'string' || !parseDateString(activatedAt)) return null;

    const components = plan.components && typeof plan.components === 'object'
        ? Object.values(plan.components as Record<string, unknown>)
        : [];
    const activityCount = components.reduce<number>((sum, component) => {
        const activities = (component as { maintenanceActivities?: unknown } | null)?.maintenanceActivities;
        return sum + (activities && typeof activities === 'object' ? Object.keys(activities).length : 0);
    }, 0);

    return {
        activatedAt,
        activatedBy: typeof metadata.activatedBy === 'string' && metadata.activatedBy ? metadata.activatedBy : undefined,
        componentCount: components.length,
        activityCount,
        isCurrent
    };
};

/**
 * One building's activations, oldest first, with re-activation runs folded
 * into their last activation; each points at the run before it.
 */
export const planActivations = (active: unknown, archive: unknown): MaintenancePlanActivation[] => {
    const plans: PlanActivationFacts[] = [];
    const current = parsePlan(active, true);
    if (current) plans.push(current);
    if (archive && typeof archive === 'object') {
        Object.values(archive as Record<string, unknown>).forEach(entry => {
            const plan = parsePlan(entry, false);
            if (plan) plans.push(plan);
        });
    }
    const date = (plan: PlanActivationFacts) => parseDateString(plan.activatedAt)!;
    plans.sort((a, b) => date(a).getTime() - date(b).getTime());

    const runs: PlanActivationFacts[][] = [];
    plans.forEach(plan => {
        const run = runs[runs.length - 1];
        if (run && withinReactivationWindow(date(run[run.length - 1]), date(plan))) {
            run.push(plan);
        } else {
            runs.push([plan]);
        }
    });

    return runs.map((run, index) => {
        const previousRun = index > 0 ? runs[index - 1] : undefined;
        return {
            ...run[run.length - 1],
            firstActivatedAt: run[0].activatedAt,
            activationCount: run.length,
            previousActivatedAt: previousRun?.[previousRun.length - 1].activatedAt
        };
    });
};

/**
 * Timeline events for every building in `buildingSummaries`. `active` and
 * `archive` are the values of maintenance/active and maintenance/archive keyed
 * by building (a single-building fetch passes a one-entry map).
 */
export const processMaintenancePlans = (
    active: Record<string, unknown> | null,
    archive: Record<string, unknown> | null,
    buildingSummaries: { [key: string]: BuildingSummary },
    events: TimelineEvent[]
): void => {
    Object.entries(buildingSummaries).forEach(([buildingKey, summary]) => {
        planActivations(active?.[buildingKey], archive?.[buildingKey]).forEach(activation => {
            events.push({
                buildingKey,
                buildingName: summary.name || 'Namnlös byggnad',
                buildingSummary: summary,
                eventType: TimelineEventType.MAINTENANCE_PLAN_ACTIVATED,
                date: parseDateString(activation.activatedAt)!,
                displayDate: activation.activatedAt,
                maintenancePlan: activation
            });
        });
    });
};
